import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { fadeInAnimation } from '../../../animations';
import { BaseContainer } from 'app/modules/base/BaseContainer';
import { HomeMarketBonusSandbox } from './home-market-bonus.sandbox';
import { HomeMarketBonus, HomeMarketBonusQuery } from '../../../core/interfaces/home-market-bonus.interface';
import { AlertService, Paging } from '@dlw-int-mwi/eps-common';
import { FormBuilder, FormGroup } from '@angular/forms';
import { HmbFilterForm } from '../home-market-bonus-filter/home-market-bonus-filter';
import { firstValueFrom } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-home-market-bonus-page',
  templateUrl: './home-market-bonus.container.html',
  styleUrls: ['./home-market-bonus.container.scss'],
  animations: [fadeInAnimation],
})
export class HomeMarketBonusContainer extends BaseContainer implements OnInit {
  sandbox = inject(HomeMarketBonusSandbox);
  private formBuilder = inject(FormBuilder);
  private alertService = inject(AlertService);

  list?: HomeMarketBonus[];
  filterForm: FormGroup<HmbFilterForm>;
  paging: Paging = {
    page: 0, // zero based
    totalCount: 0,
    itemsPerPage: 25,
    sortKey: 'activeFrom',
    sortOrder: 'DESC'
  }
  legendVisible = signal(false);
  isDisabled = computed(() => this.shipTosLoading() || this.noHmbRecordsAvailable());
  listIsLoading = signal(false);
  isDownloading = signal(false);

  private shipTosLoading = toSignal(this.sandbox.shipTosLoading$);
  private noHmbRecordsAvailable = toSignal(this.sandbox.noHmbRecordsAvailable$);

  ngOnInit() {
    this.initForm();

    this.sandbox.loadingShipTosFailed$.pipe(this.takeUntilDestroyed()).subscribe(failed => {
      if (failed) {
        this.alertService.error('HOME_MARKET_BONUS.ERRORS.SHIP_TO_LOAD_ERROR');
      }
    });
    this.sandbox.noHmbRecordsAvailable$.pipe(this.takeUntilDestroyed()).subscribe(noRecordsAvailable => {
      if (noRecordsAvailable) {
        this.alertService.error('HOME_MARKET_BONUS.ERRORS.NO_RECORDS');
      }
    });
  }

  async executeFilter(paging: Paging) {
    this.paging = paging;
    const query = await this.getQuery(paging);
    this.listIsLoading.set(true);
    this.sandbox.getHomeMarketBonuses(query).then(homeMarketBonuses => {
      this.list = homeMarketBonuses;
      this.paging = {
        ...this.paging,
        totalCount: homeMarketBonuses.length
      }
      this.cd.markForCheck();
    }).finally(() => this.listIsLoading.set(false));
  }

  private async getQuery(paging?: Paging) {
    const filter = this.filterForm.value;
    const allSenderShipToIds = (await firstValueFrom(this.sandbox.senderShipTos$)).map(definition => definition.id);
    const allReceiverShipToIds = (await firstValueFrom(this.sandbox.receiverShipTos$)).map(definition => definition.id);

    const query: HomeMarketBonusQuery = {
      senderShipToIds: filter.senderShipTos?.length ? filter.senderShipTos : allSenderShipToIds,
      receiverShipToIds: filter.receiverShipTos?.length ? filter.receiverShipTos : allReceiverShipToIds,
      receiverRoles: filter.receiverRoles,
      substring: filter.substring,
      isActive: filter.filterActive,
    }
    return query;
  }

  private async initForm() {
    this.filterForm = this.formBuilder.group({
      substring: '',
      filterActive: [true],
      senderShipTos: [],
      receiverShipTos: [],
      receiverRoles: [],
    })
  }

  async downloadList() {
    this.isDownloading.set(true);
    try {
      await this.sandbox.downloadHomeMarketBonuses(await this.getQuery());
    } finally {
      this.isDownloading.set(false);
    }
  }

  resetFilter() {
    this.filterForm.reset();
    this.filterForm.get('filterActive').setValue(true);
  }

  nameGetter(property: string) {
    return record => record[property]?.name;
  }
}
